import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { toast } from 'sonner';
import { CalendarX, Loader2 } from 'lucide-react';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BLOCK_REASONS = [
  { value: 'holiday', label: 'Holiday' },
  { value: 'meeting', label: 'Internal Meeting' },
  { value: 'vacation', label: 'Vacation' },
  { value: 'event', label: 'Company Event' },
  { value: 'maintenance', label: 'System Maintenance' },
  { value: 'other', label: 'Other' }
];

export function BlockTimeModal({ isOpen, onClose, sessionToken, onBlockCreated }) {
  const [selectedDate, setSelectedDate] = useState(null);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');
  const [reason, setReason] = useState('holiday');
  const [customReason, setCustomReason] = useState('');
  const [blockFullDay, setBlockFullDay] = useState(true);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!selectedDate) {
      toast.error('Please select a date');
      return;
    }

    try {
      setLoading(true);
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const blockReason = reason === 'other' ? customReason : BLOCK_REASONS.find(r => r.value === reason)?.label;

      // Send as JSON body (not query params)
      const requestBody = {
        date: dateStr,
        start_time: blockFullDay ? null : startTime,
        reason: blockReason || 'Blocked',
        all_day: blockFullDay
      };

      const response = await fetch(`${BACKEND_URL}/api/v1/admin/availability/block`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Blocked ${format(selectedDate, 'MMM d, yyyy')}${!blockFullDay ? ` (${startTime} - ${endTime})` : ''}`);
        onBlockCreated?.(data);
        handleClose();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create block');
      }
    } catch (error) {
      console.error('Error creating block:', error);
      toast.error(error.message || 'Failed to block time');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedDate(null);
    setStartTime('09:00');
    setEndTime('17:00');
    setReason('holiday');
    setCustomReason('');
    setBlockFullDay(true);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md" data-testid="block-time-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarX className="w-5 h-5 text-red-500" />
            Block Time Slot
          </DialogTitle>
          <DialogDescription>
            Block a date or time range to prevent bookings
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Calendar */}
          <div>
            <Label className="mb-2 block">Select Date</Label>
            <div className="flex justify-center">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                disabled={(date) => date < new Date()}
                className="rounded-md border"
                data-testid="block-date-calendar"
              />
            </div>
          </div>

          {selectedDate && (
            <div className="p-3 bg-gray-50 rounded-lg text-center">
              <span className="text-sm text-gray-600">Selected: </span>
              <span className="font-medium">{format(selectedDate, 'EEEE, MMMM d, yyyy')}</span>
            </div>
          )}

          {/* Block Type */}
          <div className="flex items-center gap-4">
            <Label className="flex items-center gap-2">
              <input
                type="radio"
                checked={blockFullDay}
                onChange={() => setBlockFullDay(true)}
                className="w-4 h-4"
                data-testid="block-full-day"
              />
              Full Day
            </Label>
            <Label className="flex items-center gap-2">
              <input
                type="radio"
                checked={!blockFullDay}
                onChange={() => setBlockFullDay(false)}
                className="w-4 h-4"
                data-testid="block-time-range"
              />
              Time Range
            </Label>
          </div>

          {/* Time Range (if not full day) */}
          {!blockFullDay && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm">Start Time</Label>
                <Input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className="mt-1"
                  data-testid="block-start-time"
                />
              </div>
              <div>
                <Label className="text-sm">End Time</Label>
                <Input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="mt-1"
                  data-testid="block-end-time"
                />
              </div>
            </div>
          )}

          {/* Reason */}
          <div>
            <Label className="mb-2 block">Reason</Label>
            <Select data-testid="block-time-modal-select-1" value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid="block-reason-select">
                <SelectValue placeholder="Select reason" />
              </SelectTrigger>
              <SelectContent>
                {BLOCK_REASONS.map((r) => (
                  <SelectItem data-testid="block-time-modal-selectitem-1" key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {reason === 'other' && (
            <div>
              <Label className="text-sm">Custom Reason</Label>
              <Input
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                placeholder="Enter reason..."
                className="mt-1"
                data-testid="block-custom-reason"
              />
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3">
          <Button data-testid="block-time-modal-button-1" variant="outline" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={!selectedDate || loading}
            className="bg-red-600 hover:bg-red-700"
            data-testid="confirm-block-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Blocking...
              </>
            ) : (
              'Block Time'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default BlockTimeModal;
